import React from 'react';
import { MetaLabel, PlayfairHeading, NobelDivider } from '../nobel';
import { TrendingDown, AlertCircle } from 'lucide-react';

interface RegressionAlertProps {
    activityType: string;
    currentScore: number;
    previousAvg: number;
    decline: number;
    onBoardGate: () => void;
    onViewHistory: () => void;
    onDismiss: () => void;
}

/**
 * Regression Alert Modal - Score Decline Warning
 * Triggers when band score drops significantly
 */
export const RegressionAlert: React.FC<RegressionAlertProps> = ({
    activityType,
    currentScore,
    previousAvg,
    decline,
    onBoardGate,
    onViewHistory,
    onDismiss
}) => {
    const getActivityName = (type: string): string => {
        const names: Record<string, string> = {
            reading: 'Reading',
            writing_t1: 'Writing Task 1',
            writing_t2: 'Writing Task 2',
            listening: 'Listening',
            speaking: 'Speaking'
        };
        return names[type] || type;
    };

    const getRecommendations = (type: string): string[] => {
        const recommendations: Record<string, string[]> = {
            reading: [
                'Practice 3 Reading sessions focusing on speed',
                'Review question types you struggled with',
                'Time yourself strictly (20 min per passage)'
            ],
            writing_t1: [
                'Review data interpretation techniques',
                'Practice describing trends accurately',
                'Focus on 150+ word requirement'
            ],
            writing_t2: [
                'Review essay structure (intro, body, conclusion)',
                'Practice opinion expression techniques',
                'Aim for 250+ words with clear arguments'
            ],
            listening: [
                'Practice with varied accents',
                'Focus on note-taking while listening',
                'Review question prediction strategies'
            ],
            speaking: [
                'Record and review your responses',
                'Practice Part 2 (2-minute speech)',
                'Work on fluency and pronunciation'
            ]
        };
        return recommendations[type] || ['Practice this module regularly', 'Review your previous high-scoring work'];
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '2rem'
        }}>
            <div style={{
                backgroundColor: 'var(--bg-secondary)',
                border: '3px solid var(--color-cancelled)',
                borderRadius: '0.5rem',
                padding: '3rem',
                maxWidth: '550px',
                width: '100%',
                boxShadow: '0 10px 30px rgba(153, 27, 27, 0.3)',
                position: 'relative'
            }}>
                {/* Icon */}
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                    <AlertCircle
                        size={64}
                        style={{
                            color: 'var(--color-cancelled)',
                            marginBottom: '1rem'
                        }}
                    />
                </div>

                {/* Title */}
                <MetaLabel style={{
                    textAlign: 'center',
                    color: 'var(--color-cancelled)',
                    marginBottom: '0.5rem'
                }}>
                    ⚠️ VISA ALERT
                </MetaLabel>

                <p className="body-inter" style={{
                    textAlign: 'center',
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    marginBottom: '2rem',
                    color: 'var(--text-primary)'
                }}>
                    Performance Decline Detected
                </p>

                <NobelDivider style={{
                    margin: '2rem auto',
                    width: '4rem',
                    backgroundColor: 'var(--color-cancelled)'
                }} />

                {/* Activity Type */}
                <MetaLabel style={{
                    textAlign: 'center',
                    display: 'block',
                    marginBottom: '1rem'
                }}>
                    {getActivityName(activityType).toUpperCase()} BAND SCORE
                </MetaLabel>

                {/* Score Comparison */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '2rem',
                    marginBottom: '2rem'
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <MetaLabel style={{ fontSize: '0.625rem', marginBottom: '0.5rem' }}>
                            PREVIOUS AVG
                        </MetaLabel>
                        <PlayfairHeading size="4xl" style={{
                            margin: 0,
                            color: 'var(--color-stone-500)'
                        }}>
                            {previousAvg.toFixed(1)}
                        </PlayfairHeading>
                    </div>

                    <TrendingDown size={48} style={{ color: 'var(--color-cancelled)' }} />

                    <div style={{ textAlign: 'center' }}>
                        <MetaLabel style={{ fontSize: '0.625rem', marginBottom: '0.5rem' }}>
                            CURRENT
                        </MetaLabel>
                        <PlayfairHeading size="4xl" style={{
                            margin: 0,
                            color: 'var(--color-cancelled)'
                        }}>
                            {currentScore.toFixed(1)}
                        </PlayfairHeading>
                    </div>
                </div>

                {/* Decline Info */}
                <div style={{
                    backgroundColor: 'var(--bg-elevated)',
                    border: '1px solid var(--color-cancelled)',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    marginBottom: '2rem',
                    textAlign: 'center'
                }}>
                    <MetaLabel style={{ color: 'var(--color-cancelled)', fontSize: '0.75rem' }}>
                        DECLINE: -{decline.toFixed(1)} BANDS
                    </MetaLabel>
                </div>

                {/* Message */}
                <p className="body-inter" style={{
                    textAlign: 'center',
                    marginBottom: '2rem',
                    color: 'var(--text-body)',
                    lineHeight: '1.6'
                }}>
                    Your recent {getActivityName(activityType)} performance has declined.
                    Immigration officers have flagged this regression.
                </p>

                {/* Recommendations */}
                <div style={{
                    backgroundColor: 'var(--bg-elevated)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: '0.5rem',
                    padding: '1.5rem',
                    marginBottom: '2rem'
                }}>
                    <MetaLabel style={{ marginBottom: '1rem', display: 'block' }}>
                        RECOMMENDED ACTION
                    </MetaLabel>
                    <ul style={{
                        listStyle: 'none',
                        padding: 0,
                        margin: 0
                    }}>
                        {getRecommendations(activityType).map((rec, idx) => (
                            <li
                                key={idx}
                                className="body-inter"
                                style={{
                                    padding: '0.5rem 0',
                                    color: 'var(--text-body)',
                                    fontSize: '0.875rem',
                                    display: 'flex',
                                    gap: '0.5rem'
                                }}
                            >
                                <span style={{ color: 'var(--color-nobel-gold)' }}>•</span>
                                {rec}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                    <button
                        className="btn-primary"
                        onClick={onBoardGate}
                        style={{ flex: 1 }}
                    >
                        BOARD GATE NOW
                    </button>
                    <button
                        className="btn-secondary"
                        onClick={onViewHistory}
                        style={{ flex: 1 }}
                    >
                        VIEW HISTORY
                    </button>
                </div>

                {/* Dismiss */}
                <div style={{ textAlign: 'center' }}>
                    <button
                        onClick={onDismiss}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.5rem'
                        }}
                    >
                        <MetaLabel style={{ fontSize: '0.625rem' }}>
                            DISMISS
                        </MetaLabel>
                    </button>
                </div>
            </div>
        </div>
    );
};
